/**
 * =============================================================================
 * INTERPRETACION CLINICA: SU COLOR Y SU TEXTO
 * =============================================================================
 * DE DONDE VIENE ESTO. El color se decidia buscando las palabras "leve",
 * "moderado", "severo", "grave", "dependiente" e "independiente" dentro del
 * texto. El calculador no emite NINGUNA de ellas —ese vocabulario es el del
 * nivel de dificultad de una escena—, asi que nueve de las diez
 * interpretaciones salian azules y "Depresión Establecida" se veia igual que
 * "Puntuación acumulada".
 *
 * DESPUES se indexo por el texto en castellano, que era mejor pero seguia
 * atando el color a una cadena que alguien puede reescribir.
 *
 * AHORA se indexa por `interpretationCode`, que el servidor emite desde el
 * 2026-08-10 y es identidad estable. El texto pasa a ser presentacion.
 *
 * ---------------------------------------------------------------------------
 * TRES DE LAS DIEZ NO SON UN VEREDICTO
 * ---------------------------------------------------------------------------
 * "Ver desglose por dimensiones", "Valoración Manual" y "Puntuación acumulada"
 * no dicen si al paciente le va bien o mal: dicen que ahi no hay
 * interpretacion. Van en gris, porque pintarlas de un color sugeriria un juicio
 * clinico que nadie ha emitido.
 *
 * Lo desconocido tambien cae en gris. Si manana aparece un codigo nuevo, es
 * mejor que se vea neutro a que herede un color que nadie ha decidido.
 *
 * ---------------------------------------------------------------------------
 * LA CAIDA HACIA ATRAS, QUE NO ES OPCIONAL
 * ---------------------------------------------------------------------------
 * Hay **129 resultados guardados antes de que existiera el codigo**. No lo
 * tienen, y eso no es un dato incompleto: es que se calcularon antes. Para
 * ellos se sigue usando el texto, exactamente como hasta ahora.
 *
 *   con codigo     -> por codigo (estable, traducible)
 *   sin codigo     -> por texto  (los 129 de antes)
 *   ninguno de los dos -> gris
 *
 * En ningun caso queda un hueco en pantalla.
 * =============================================================================
 */

/** Gris de Ant Design: presente, legible, sin sugerir nada. */
export const COLOR_NEUTRO = "default";

/**
 * Color por codigo.
 *
 * La GRAVEDAD canonica —que es una decision clinica— vive en el servidor, en
 * `server/utils/revi/interpretationCodes.js`. Esta tabla solo decide el COLOR,
 * que es presentacion. Un test del servidor comprueba que no falte ningun
 * codigo aqui.
 */
const COLOR_POR_CODIGO = {
  // --- GDS-15. El orden clinico es sin < probable < establecida -------------
  sin_depresion: "green",
  depresion_probable: "orange",
  depresion_establecida: "red",

  // --- NPT-ES. Tres escalones, tres colores ---------------------------------
  // "Buena" no comparte verde con "Excelente": serian indistinguibles de un
  // vistazo, que es justo el fallo que se vino a arreglar.
  adaptacion_excelente: "green",
  adaptacion_buena: "cyan",
  adaptacion_baja: "orange",

  // --- Los que NO son un veredicto ------------------------------------------
  ver_dimensiones: COLOR_NEUTRO,
  valoracion_manual: COLOR_NEUTRO,
  puntuacion_acumulada: COLOR_NEUTRO,
  sin_calculo: COLOR_NEUTRO,
};

/** Clave de comparacion: sin tildes, sin mayusculas, sin espacios de sobra. */
function claveDe(texto) {
  return String(texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Solo para los resultados anteriores al codigo. No se amplia: lo que venga de
 * ahora en adelante trae codigo.
 */
const COLOR_POR_TEXTO_ANTIGUO = {
  normal: "green",
  "probable depresion": "orange",
  "depresion establecida": "red",
  "adaptacion excelente": "green",
  "adaptacion buena": "cyan",
  "nivel de adaptacion bajo": "orange",
  "ver desglose por dimensiones": COLOR_NEUTRO,
  "valoracion manual": COLOR_NEUTRO,
  "puntuacion acumulada": COLOR_NEUTRO,
  "sin calculo definido": COLOR_NEUTRO,
};

/**
 * Color del tag para una interpretacion.
 *
 * @param {string} interpretacion el texto que devuelve el servidor
 * @param {string} [codigo]       `processedScore.interpretationCode`
 */
export function colorDeInterpretacion(interpretacion, codigo) {
  if (codigo && COLOR_POR_CODIGO[codigo]) return COLOR_POR_CODIGO[codigo];
  if (!interpretacion || interpretacion === "-") return COLOR_NEUTRO;
  return COLOR_POR_TEXTO_ANTIGUO[claveDe(interpretacion)] || COLOR_NEUTRO;
}

/**
 * El texto de una interpretacion, en el idioma de la interfaz.
 *
 * @param {string} interpretacion lo que devuelve el servidor, en castellano
 * @param {string} [codigo]       `processedScore.interpretationCode`
 * @param {Function} t            traductor del namespace `evaluations`
 *
 * ORDEN DE PREFERENCIA, y cada escalon existe por un motivo:
 *
 *   1. codigo con traduccion  -> se traduce
 *   2. codigo sin traduccion  -> el texto del servidor. Pasa cuando entra una
 *      escala nueva y su traduccion llega mas tarde; mejor castellano que una
 *      clave en bruto en la pantalla de un terapeuta.
 *   3. sin codigo             -> el texto del servidor. Son los 129 de antes.
 */
export function textoDeInterpretacion(interpretacion, codigo, t) {
  if (codigo) {
    // El namespace va explicito en la clave para que esta funcion sirva desde
    // cualquier componente, tenga cargado el que tenga. Quien la use solo debe
    // asegurarse de incluir "evaluations" en su `useTranslation`.
    const clave = `evaluations:interpretaciones.${codigo}`;
    const traducido = t(clave);
    // i18next devuelve la clave misma cuando no la encuentra.
    if (traducido && traducido !== clave) return traducido;
  }
  return interpretacion || "—";
}

/** Para los tests y para el dia que haya que revisar la tabla entera. */
export const CODIGOS_CONOCIDOS = Object.keys(COLOR_POR_CODIGO);
